import { type QueryClient, useMutation } from '@tanstack/react-query';
import {
  type AudioItem,
  AudioItems,
  audioItemsBaseKey,
  type UpdateAudioPayload,
} from './audio-items';
import {
  getAudioFromAnyCache,
  useAudioWalkLifecycle,
  useUnarchiveAudioLifecycle,
} from './db-interim/walk-audio-caches';
import { useEq } from './runtime-context';

// Shared optimistic audio mutations (start / mobile / browser-ext): the typed
// Effect program (via `useEq().mutationOptions`) paired with the shared audio
// cache walk (`useAudioWalkLifecycle`) — optimistic patch across every audio
// cache, rollback on error, prefix-invalidate on settle. App callsites add
// their own `onSuccess` (toast / navigate / analytics) on top.

export type UpdateAudioVariables = {
  audioId: string;
  updates: UpdateAudioPayload;
};

type WritableAudioPatch = {
  -readonly [K in keyof AudioItem]?: AudioItem[K];
};

// Derive the optimistic row patch from an update payload. `visibility` lives at
// `sharing.visibility`, so rebuild `sharing` from the current cached row.
export const updatePatchFields = (
  { audioId, updates }: UpdateAudioVariables,
  queryClient: QueryClient,
): Partial<AudioItem> => {
  const partial: WritableAudioPatch = {};
  if ('title' in updates) partial.title = updates.title ?? null;
  if ('description' in updates)
    partial.description = updates.description ?? null;
  if ('tags' in updates) partial.tags = updates.tags ?? [];
  if ('visibility' in updates) {
    const current = getAudioFromAnyCache(queryClient, audioId);
    if (current?.sharing) {
      partial.sharing = {
        ...current.sharing,
        visibility: updates.visibility ?? 'private',
      };
    }
  }
  return partial;
};

// PATCH audio metadata. `updates` already matches the API PATCH shape, so it
// goes straight to the server; the optimistic row patch is derived via the walk.
export const useUpdateAudioMutation = () =>
  useMutation({
    ...useEq().mutationOptions({
      mutationKey: [...audioItemsBaseKey, 'update'],
      mutationFn: ({ audioId, updates }: UpdateAudioVariables) =>
        AudioItems.updateEffect(audioId, updates),
    }),
    ...useAudioWalkLifecycle<UpdateAudioVariables>((v, queryClient) => ({
      kind: 'patchFields',
      id: v.audioId,
      partial: updatePatchFields(v, queryClient),
    })),
  });

// Status flip via the separate `unarchiveAudioEffect` endpoint. Unarchiving is a
// cross-list MEMBERSHIP move (out of the ready lists, into the processing queue
// as `initializing`), not an in-place field edit, so it uses the dedicated
// `useUnarchiveAudioLifecycle` rather than the `patchFields` walk — see its doc
// in walk-audio-caches.ts. onSettled invalidates so the server reconciles.
export const useUnarchiveAudioMutation = () =>
  useMutation({
    ...useEq().mutationOptions({
      mutationKey: [...audioItemsBaseKey, 'unarchive'],
      mutationFn: (audioId: string) => AudioItems.unarchiveEffect(audioId),
    }),
    ...useUnarchiveAudioLifecycle(),
  });
